import { lazy, Suspense, memo, useEffect, useState, useRef } from "react";
import styles from "./icon.module.scss";
import { v4 as uuidv4 } from "uuid"; // uuid 가져오기

interface IconProps {
  folderName?: string;
  name?: string;
  size?: number | "fill";
  IconComponent?: React.ReactNode;
  styleTag?: string;
  [key: string]: any;
}

const Icon = memo(
  ({ folderName, name, size = 24, IconComponent, styleTag, ...otherProps }: IconProps) => {
    const ref = useRef<HTMLDivElement>(null);
    const uniqueClassName = `icon-${name}-${uuidv4()}`; // UUID 기반 유니크한 클래스명 생성

    // 아이콘 로드 상태
    const [isLoaded, setIsLoaded] = useState<boolean>(IconComponent ? true : false);

    // 동적 아이콘 컴포넌트
    const [DynamicIconComponent, setDynamicIconComponent] = useState<React.ComponentType<{
      [key: string]: any;
    }> | null>(null);

    // 동적 import props
    const DynamicProps: {
      [key: string]: any;
    } = {
      width: size,
      height: size,
      ...otherProps,
      className: `${otherProps?.className ? otherProps.className + " " + uniqueClassName : uniqueClassName}`,
    };

    useEffect(() => {
      let isMounted = true; // 마운트 여부 추적

      if (!IconComponent && name && size !== "fill") {
        // 동적 import
        import(`./${folderName ? folderName : size}/${name}`)
          .then((module) => {
            if (isMounted) {
              setDynamicIconComponent(() => module.default); // 동적 컴포넌트 설정
              setIsLoaded(true); // 로드 완료 상태 설정
            }
          })
          .catch(() => {
            if (isMounted) {
              console.error(`아이콘 로드 실패 ./${folderName ? folderName : size}/${name}`);
              setIsLoaded(false); // 로드 실패 상태 설정
            }
          })
          .finally(() => {
            // const icon = ref.current
            // if (icon) {
            // 	console.log(icon.querySelector('path'))
            // }
          });
      }

      // 언마운트 시 isMounted를 false로 설정
      return () => {
        isMounted = false;
      };
    }, [IconComponent, folderName, name, size]);

    return (
      <i
        className={`${styles.icon} ${styles["icon-" + size]} 
				${styles["icon-component"]} ${isLoaded ? styles["icon-loaded"] : ""}`}
        ref={ref}
      >
        {/* 동적으로 style 태그 추가 */}
        {styleTag ? (
          <style>{`
						.${uniqueClassName} {
							${styleTag}
						}
					`}</style>
        ) : null}
        {IconComponent ? (
          IconComponent
        ) : name && DynamicIconComponent ? (
          <Suspense fallback={null}>
            <DynamicIconComponent {...DynamicProps} />
          </Suspense>
        ) : null}
      </i>
    );
  },
);

Icon.displayName = "Icon";
export default Icon;
